import React, { useState } from 'react';
import { Table, Button, Badge } from 'react-bootstrap';
import { updateTenantStatus } from '../../api/api';
import CreateTenantModal from './CreateTenantModal';
import TenantAdminsModal from './TenantAdminsModal';
import { showToast } from '../../utils/toast';

const MODULE_SHORT = {
  examination: 'ExamOS', teaching_copilot: 'Copilot',
  AskOS: 'AskOS', content_generation: 'ContentGen', assessment_bank: 'AssessBank',
};
const MODULE_COLOR = {
  examination: 'primary', teaching_copilot: 'info',
  AskOS: 'warning', content_generation: 'success', assessment_bank: 'danger',
};

const TenantTable = ({ tenants, onRefresh, onRecharge, onManageModules }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const toggleStatus = async (tenant) => {
    const isActive = tenant.status === 'active';

    // Business rule:
    // active -> suspended
    // suspended / trial -> active
    const newStatus = isActive ? 'suspended' : 'active';

    try {
      setUpdatingId(tenant.id);
      await updateTenantStatus(tenant.id, newStatus);
      await onRefresh();
    } catch (err) {
      console.error('Failed to update tenant status', err);
      showToast('Failed to update tenant status. Check logs.', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">active</Badge>;
      case 'trial':
        return <Badge bg="warning" text="dark">trial</Badge>;
      case 'suspended':
        return <Badge bg="danger">suspended</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          Create Tenant
        </Button>
      </div>

      <Table bordered hover size="sm" responsive className="align-middle">
        <thead className="table-light">
          <tr>
            <th>Tenant Name</th>
            <th>Slug</th>
            <th>Group</th>
            <th>Status</th>
            <th>Subscribed Modules</th>
            <th>Wallet Balance</th>
            <th scope="col" style={{ width: '320px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tenants.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center text-muted">
                No tenants found
              </td>
            </tr>
          )}

          {tenants.map((tenant) => {
            const parent = tenant.parent_tenant_id
              ? tenants.find(t => t.id === tenant.parent_tenant_id)
              : null;
            const centreCount = tenants.filter(t => t.parent_tenant_id === tenant.id).length;
            return (
            <tr key={tenant.id}>
              <td>{tenant.name}</td>
              <td>
                <code>{tenant.slug}</code>
              </td>
              <td>
                {parent && <Badge bg="light" text="dark" className="border">Centre of {parent.name}</Badge>}
                {!parent && centreCount > 0 && <Badge bg="light" text="dark" className="border">{centreCount} centre(s)</Badge>}
                {!parent && centreCount === 0 && <span className="text-muted">—</span>}
              </td>
              <td>{renderStatusBadge(tenant.status)}</td>
              <td>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {(tenant.modules || []).map((m) => (
                    <Badge key={m} bg={MODULE_COLOR[m] || 'secondary'} style={{ fontSize: 9, fontWeight: 700 }}>
                      {MODULE_SHORT[m] || m}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                <strong className={tenant.wallet_balance < 10 ? "text-danger" : "text-dark"}>
                  {tenant.wallet_balance !== undefined ? `${tenant.wallet_balance} Credits` : 'N/A'}
                </strong>
              </td>
              <td className="d-flex gap-1 flex-wrap">
                {/* Suspend / Activate */}
                <Button
                  size="sm"
                  variant={
                    tenant.status === 'active'
                      ? 'outline-danger'
                      : 'outline-success'
                  }
                  onClick={() => toggleStatus(tenant)}
                  disabled={updatingId === tenant.id}
                  style={{ minWidth: '85px' }}
                >
                  {updatingId === tenant.id
                    ? '...'
                    : tenant.status === 'active'
                      ? 'Suspend'
                      : 'Activate'}
                </Button>

                {/* Manage Tenant Admins */}
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setSelectedTenant(tenant)}
                >
                  Admins
                </Button>

                <Button variant="success" size="sm" onClick={() => onRecharge(tenant)} title="Recharge Credits">
                  <i className="bi bi-wallet2"></i> Recharge
                </Button>
                <Button variant="outline-info" size="sm" onClick={() => onManageModules && onManageModules(tenant)} title="Manage module subscriptions">
                  🧩 Modules
                </Button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Create Tenant Modal */}
      <CreateTenantModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        tenants={tenants}
        onCreated={onRefresh}
      />

      {/* Tenant Admins Modal */}
      {selectedTenant && (
        <TenantAdminsModal
          show={!!selectedTenant}
          tenant={selectedTenant}
          onHide={() => setSelectedTenant(null)}
        />
      )}
    </>
  );
};

export default TenantTable;
